import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, SxProps, Theme, useMediaQuery, useTheme } from '@mui/material'
import React, { useRef, useState } from 'react'

const MIN_WIDTH = 320
const HANDLE_WIDTH = 20

type Props = {
  children: React.ReactNode
  // Receives the scrollable content element (used by the chapter autoscroll).
  scrollRef?: (el: HTMLDivElement | null) => void
  contentSx?: SxProps<Theme>
}

// A centered reading column whose width is set by dragging its thick left/right
// edges. The grabbed edge tracks the mouse 1:1; the column stays centered, so
// the left/right whitespace grows symmetrically. Full width on first render.
// On mobile the handles are hidden and the column always fills the full width.
export const ResizableReader: React.FC<Props> = ({
  children,
  scrollRef,
  contentSx
}) => {
  const outerRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | null>(null) // null = full width
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const startDrag = (e: React.PointerEvent) => {
    if (isMobile) return
    e.preventDefault()
    const outer = outerRef.current
    if (!outer) return
    const rect = outer.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2
    document.body.style.userSelect = 'none'

    const move = (ev: PointerEvent) => {
      // Distance from the centre, doubled -> the dragged edge sits under the cursor.
      const next = Math.min(
        rect.width,
        Math.max(MIN_WIDTH, 2 * Math.abs(ev.clientX - centerX))
      )
      setWidth(next)
    }
    const up = () => {
      document.body.style.userSelect = ''
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const handleSx: SxProps<Theme> = {
    flex: `0 0 ${HANDLE_WIDTH}px`,
    cursor: 'ew-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1,
    color: 'text.secondary',
    bgcolor: 'action.hover',
    transition: 'color 120ms, background-color 120ms',
    '&:hover': { color: 'primary.main', bgcolor: 'action.selected' }
  }

  if (isMobile) {
    return (
      <Box
        ref={scrollRef}
        sx={{ flex: 1, minWidth: 0, overflow: 'auto', px: 2, ...contentSx }}
      >
        {children}
      </Box>
    )
  }

  return (
    <Box
      ref={outerRef}
      sx={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          minHeight: 0,
          width: width ?? '100%',
          maxWidth: '100%',
          minWidth: MIN_WIDTH
        }}
      >
        <Box sx={handleSx} onPointerDown={startDrag}>
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box
          ref={scrollRef}
          sx={{ flex: 1, minWidth: 0, overflow: 'auto', px: 2, ...contentSx }}
        >
          {children}
        </Box>
        <Box sx={handleSx} onPointerDown={startDrag}>
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    </Box>
  )
}
